import {Component, inject, OnInit} from "@angular/core";
import {FormBuilder, ReactiveFormsModule} from "@angular/forms";
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatButtonModule} from "@angular/material/button";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatIconModule} from "@angular/material/icon";
import {MatTooltipModule} from "@angular/material/tooltip";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {CommonModule} from "@angular/common";
import {Store} from "@ngrx/store";
import {Observable} from "rxjs";
import {ReturnSummaryActions} from "../../../fund/store/fund.actions";
import {DealReturnSummaryActions} from "../../../deal/store/deal.actions";
import {DecimalFormatPipe} from "../../pipes/decimal-format/decimal-format.pipe";
import {selectGoalSeekLoading} from "../../../fund/store/fund-valuation/fund-valuation.selectors";
import {selectDealGoalSeekLoading} from "../../../deal/store/deal/deal.selectors";

export interface GoalSeekDialogData {
    mode: "fund" | "deal";
    hasDeal: boolean;
    goalSeekTvpi?: number;
    goalSeekTvpiActive: boolean;
    goalSeekIrr?: number;
    goalSeekIrrActive: boolean;
    goalSeekDiscount?: number;
    goalSeekDiscountActive: boolean;
    // Fund mode with deal: deal-level values for side panel
    dealGoalSeekTvpi?: number;
    dealGoalSeekIrr?: number;
    dealGoalSeekDiscount?: number;
}

@Component({
    selector: "valumize-goal-seek-dialog",
    templateUrl: "./goal-seek-dialog.component.html",
    styleUrls: ["./goal-seek-dialog.component.scss"],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatCheckboxModule,
        MatIconModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        DecimalFormatPipe,
    ],
})
export class GoalSeekDialogComponent implements OnInit {
    private readonly formBuilder = inject(FormBuilder);
    private readonly store = inject(Store);
    readonly data = inject<GoalSeekDialogData>(MAT_DIALOG_DATA);
    readonly dialogRef = inject(MatDialogRef<GoalSeekDialogComponent>);

    readonly goalSeekLoading$: Observable<boolean> = this.data.mode === "fund"
        ? this.store.select(selectGoalSeekLoading)
        : this.store.select(selectDealGoalSeekLoading);

    readonly activeTooltip = "Select TVPI & IRR to run to lower-of (i.e. >= both conditions). Otherwise, select a single metric to run goal-seek to that metric";

    form = this.formBuilder.group({
        tvpi: this.formBuilder.control<string | null>(null),
        tvpiActive: this.formBuilder.control<boolean>(false),
        irr: this.formBuilder.control<string | null>(null),
        irrActive: this.formBuilder.control<boolean>(false),
        discount: this.formBuilder.control<string | null>(null),
        discountActive: this.formBuilder.control<boolean>(false),
    });

    ngOnInit(): void {
        this.form.patchValue({
            tvpi: this.data.goalSeekTvpi != null ? this.data.goalSeekTvpi.toFixed(2) : null,
            tvpiActive: this.data.goalSeekTvpiActive,
            irr: DecimalFormatPipe.transformFractionToPercent(this.data.goalSeekIrr)?.toFixed(2) ?? null,
            irrActive: this.data.goalSeekIrrActive,
            discount: DecimalFormatPipe.transformFractionToPercent(this.data.goalSeekDiscount)?.toFixed(2) ?? null,
            discountActive: this.data.goalSeekDiscountActive,
        });
    }

    get isNoTargetActive(): boolean {
        const v = this.form.value;
        return !v.tvpiActive && !v.irrActive && !v.discountActive;
    }

    get hasActiveTargetWithoutValue(): boolean {
        const v = this.form.value;
        return (v.tvpiActive === true && !v.tvpi)
            || (v.irrActive === true && !v.irr)
            || (v.discountActive === true && !v.discount);
    }

    onTvpiOrIrrActiveChange(checked: boolean): void {
        if (checked) {
            this.form.patchValue({discountActive: false});
        }
    }

    onDiscountActiveChange(checked: boolean): void {
        if (checked) {
            this.form.patchValue({tvpiActive: false, irrActive: false});
        }
    }

    resetToDealDefault(): void {
        this.form.patchValue({
            tvpi: this.data.dealGoalSeekTvpi != null ? this.data.dealGoalSeekTvpi.toFixed(2) : null,
            irr: DecimalFormatPipe.transformFractionToPercent(this.data.dealGoalSeekIrr)?.toFixed(2) ?? null,
            discount: DecimalFormatPipe.transformFractionToPercent(this.data.dealGoalSeekDiscount)?.toFixed(2) ?? null,
        });
    }

    goalSeek(): void {
        const values = this.form.value;
        const params = {
            targetTvpi: values.tvpi ? parseFloat(values.tvpi) : undefined,
            targetTvpiActive: values.tvpiActive ?? false,
            targetIrr: DecimalFormatPipe.transformPercentToFraction(values.irr ? parseFloat(values.irr) : undefined),
            targetIrrActive: values.irrActive ?? false,
            targetDiscount: DecimalFormatPipe.transformPercentToFraction(values.discount ? parseFloat(values.discount) : undefined),
            targetDiscountActive: values.discountActive ?? false,
        };

        if (this.data.mode === "fund") {
            this.store.dispatch(ReturnSummaryActions.goalSeek(params));
        } else {
            this.store.dispatch(DealReturnSummaryActions.goalSeek(params));
        }
    }
}
